import React, { memo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { assessmentPhoneLandscapeQuery, assessmentShortViewportQuery } from "../assessment-layout-styles";
import {
  formatAssessmentCountdown,
  getAssessmentCountdownDays,
  getAssessmentCountdownDayLabel,
  shouldShowAssessmentCountdownDays,
} from "../../../utils/assessmentTimeLeft";
import {
  containerStyle,
  circularContainerStyle,
  circularProgressStyle,
  circularProgressOverlayStyle,
  circularTextContainerStyle,
  circularTextStyle,
  circularDaysNumberStyle,
  circularDaysLabelStyle,
  resultsContainerStyle,
  timeLabelStyle,
} from "./styles";

interface QuizCountdownCircleProps {
  remainingSeconds: number;
  totalSeconds: number;
  isFinish?: boolean;
}

export const QuizCountdownCircle: React.FC<QuizCountdownCircleProps> = memo(
  ({ remainingSeconds, totalSeconds, isFinish = false }) => {
    const theme = useTheme();
    const isCompactTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isPhoneLandscape = useMediaQuery(assessmentPhoneLandscapeQuery);
    const isShortViewport = useMediaQuery(assessmentShortViewportQuery);
    const isTabletPortrait = useMediaQuery(
      "(orientation: portrait) and (min-width: 600px) and (max-width: 900px)",
    );

    const progress =
      totalSeconds > 0 ? (remainingSeconds / totalSeconds) * 100 : 0;
    const showDaysFormat = shouldShowAssessmentCountdownDays(remainingSeconds);
    const showHoursFormat =
      !showDaysFormat && remainingSeconds >= 3_600;

    const ringThickness = isShortViewport || isPhoneLandscape ? 4 : 6;
    const overlayThickness = isShortViewport || isPhoneLandscape ? 3 : 5;

    const circleSize = isFinish
      ? 80
      : isPhoneLandscape
        ? showDaysFormat
          ? 52
          : showHoursFormat
            ? 54
            : 52
        : isShortViewport
          ? showDaysFormat
            ? 50
            : showHoursFormat
              ? 54
              : 50
          : showDaysFormat && isTabletPortrait
            ? 66
            : showHoursFormat && isTabletPortrait
              ? 72
              : isCompactTablet
                ? 60
                : 66;

    return (
      <Box sx={containerStyle(isFinish)}>
        <Box sx={circularContainerStyle}>
          <CircularProgress
            variant="determinate"
            value={progress}
            size={circleSize}
            thickness={ringThickness}
            sx={circularProgressStyle(remainingSeconds, totalSeconds)}
          />
          <CircularProgress
            variant="determinate"
            value={100}
            size={circleSize}
            thickness={overlayThickness}
            sx={circularProgressOverlayStyle}
          />
          <Box sx={circularTextContainerStyle(theme, isShortViewport)}>
            {showDaysFormat ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  component="div"
                  sx={circularDaysNumberStyle(
                    isFinish,
                    remainingSeconds,
                    totalSeconds,
                    isCompactTablet,
                    isShortViewport,
                  )}
                >
                  {getAssessmentCountdownDays(remainingSeconds)}
                </Typography>
                <Typography
                  component="div"
                  sx={circularDaysLabelStyle(
                    isFinish,
                    remainingSeconds,
                    totalSeconds,
                    isCompactTablet,
                    isShortViewport,
                  )}
                >
                  {getAssessmentCountdownDayLabel(remainingSeconds)}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="caption"
                component="div"
                sx={circularTextStyle(
                  isFinish,
                  remainingSeconds,
                  totalSeconds,
                  showHoursFormat,
                  isCompactTablet,
                  showDaysFormat,
                  isShortViewport,
                )}
              >
                {formatAssessmentCountdown(remainingSeconds)}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={resultsContainerStyle(isFinish)}>
          <Typography
            variant="body2"
            sx={timeLabelStyle(isFinish, theme, remainingSeconds, totalSeconds)}
          >
            Time left
          </Typography>
        </Box>
      </Box>
    );
  }
);

QuizCountdownCircle.displayName = "QuizCountdownCircle";

export default QuizCountdownCircle;
